import {
  TextInput,
  Box,
  ScrollArea,
  Popover,
  Card,
  Flex,
  Text,
  Spoiler,
  Skeleton,
  Stack,
  Tooltip,
  Checkbox,
  ActionIcon,
} from "@mantine/core";
import {IconArrowUpRight, IconMessage, IconSearch, IconX} from "@tabler/icons";
import { useEffect, useState, useRef } from "react";

import { useAuth0 } from "@auth0/auth0-react";
import { useClickOutside } from "@mantine/hooks";
import { searchIdeas } from "../../../Api";
import "./SidebarLayout.css";
import {useNavigate} from "react-router-dom";
import { useCurrentUser } from "../../../hooks/useCurrentUser";

function stripHtmlFromIdeaDescription(value: unknown): string {
  if (value == null) return "";
  const s = typeof value === "string" ? value : String(value);
  return s.replace(/<[^>]*>/g, "");
}

const Search = ({
  query,
  setQuery,
  variant = "header",
  onClose,
}: {
  query: string;
  setQuery: React.Dispatch<React.SetStateAction<string>>;
  variant?: "header" | "sidebar";
  /** Called when user selects a result (e.g. close parent popover) */
  onClose?: () => void;
}) => {
  const auth0 = useAuth0();
  const currentUser = useCurrentUser();

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [opened, setOpened] = useState(false);
  const [loadingSearchResults, setLoadingSearchResults] = useState(false);
  const [onlyMyIdeas, setOnlyMyIdeas] = useState(false);
  const ref = useClickOutside(() => setOpened(false));
  const userId = currentUser?.id;
  const sidebarInputContainerRef = useRef<HTMLDivElement>(null);

  const navigate = useNavigate();
  const isSidebar = variant === "sidebar";
  const hasQuery = query.trim().length > 0;

  const doSearch = async () => {
    setLoadingSearchResults(true);
    const onlyMy = isSidebar ? undefined : onlyMyIdeas ? userId : undefined;
    searchIdeas({ query, onlyMyIdeas: onlyMy }, auth0)
      .then((results: any[]) => {
        setSearchResults(results || []);
        setLoadingSearchResults(false);
      })
      .catch((e) => {
        console.log(e);
        setLoadingSearchResults(false);
      });
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (query !== "") {
        doSearch();
      } else {
        setSearchResults([]);
      }
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [query, onlyMyIdeas, isSidebar]);

  useEffect(() => {
    if (!isSidebar) return;
    const t = window.setTimeout(() => {
      sidebarInputContainerRef.current?.querySelector?.("input")?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [isSidebar]);

  const handleResultNavigate = () => {
    onClose?.();
    if (!isSidebar) setOpened(false);
    setQuery("");
  };

  const inputEl = (
    <TextInput
      className={isSidebar ? undefined : "sidebar-search-input-header"}
      classNames={
        isSidebar
          ? {
            root: "sidebar-search-input-root",
            input: "sidebar-search-input-field",
          }
          : undefined
      }
      placeholder={isSidebar ? "Search Ideas..." : "Search"}
      variant={isSidebar ? "default" : "filled"}
      radius={isSidebar ? 0 : "md"}
      rightSectionWidth={isSidebar ? 40 : undefined}
      icon={<IconSearch size={isSidebar ? 18 : 14.5} color={isSidebar ? "#161832" : undefined} stroke={isSidebar ? 1.5 : 2} />}
      rightSection={
        isSidebar && query.length > 0 ? (
          <ActionIcon
            variant="transparent"
            size="sm"
            onClick={(e) => {
              e.stopPropagation();
              setQuery("");
            }}
            aria-label="Clear search"
          >
            <IconX size={18} color="#161832" stroke={1.5} />
          </ActionIcon>
        ) : null
      }
      value={query}
      onFocus={!isSidebar ? () => setOpened(true) : undefined}
      onClick={!isSidebar ? () => setOpened(true) : undefined}
      onChange={(event) => setQuery(event.currentTarget.value)}
    />
  );

  if (isSidebar) {
    return (
      <Box className="sidebar-search-panel">
        <Flex ref={sidebarInputContainerRef} align="center" px={12} className="sidebar-search-input-row">
          {inputEl}
        </Flex>

        {hasQuery && (
          <>
            <Box className="sidebar-search-divider" />
            <Box className="sidebar-search-results-bg">
              <ScrollArea
                style={{ maxHeight: "min(360px, 45vh)", overflowY: "auto" }}
                styles={{ viewport: { overflowY: "auto", overflowX: "hidden" } }}
              >
                {loadingSearchResults ? (
                  <Stack spacing="sm" px="md" py="sm">
                    {[1, 2, 3].map((i) => (
                      <Skeleton key={i} height={88} radius="md" />
                    ))}
                  </Stack>
                ) : searchResults.length === 0 ? (
                  <Box py={28} px="md">
                    <Text ta="center" size="sm" c="dimmed" fw={500}>
                      No results found
                    </Text>
                  </Box>
                ) : (
                  <Box px="md" py="sm">
                    {searchResults.map((idea: any) => (
                      <IdeaSearchResult
                        key={idea._id}
                        idea={idea}
                        variant="sidebar"
                        onClick={() => {
                          handleResultNavigate();
                          navigate(`/idea/${idea._id}`);
                        }}
                      />
                    ))}
                  </Box>
                )}
              </ScrollArea>
              {!loadingSearchResults && searchResults.length > 0 && (
                <Box
                  component="div"
                  px="md"
                  py={10}
                  className="sidebar-search-footer-bar"
                >
                  <Text fz={13} fw={500} c="#212529">
                    {searchResults.length} results found
                  </Text>
                </Box>
              )}
            </Box>
          </>
        )}
      </Box>
    );
  }

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      width={500}
      position="bottom-end"
      offset={20}
    >
      <Popover.Target>
        <Tooltip label="Search Ideas across the organization" withArrow>
          {inputEl}
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={0} ml={66}>
        <Box ref={ref} bg="#F8F8FB">
          <Checkbox
            className="sidebar-search-header-checkbox"
            label="Only search Ideas I own"
            color="indigo"
            checked={onlyMyIdeas}
            onChange={(event) => setOnlyMyIdeas (event.currentTarget.checked)}
            py="xs"
          />
          <ScrollArea
            style={{ height: 300, overflowX: "unset" }}
            px={"md"}
            pb={0}
          >
            { !loadingSearchResults?
              searchResults.map((idea: any) => (
                <IdeaSearchResult
                  key={idea._id}
                  idea={idea}
                  onClick={() => {
                    handleResultNavigate();
                    navigate(`/idea/${idea._id}`);
                  }}
                />
              ))
              :
              <Stack w="99%"  mt={8} mr={123}>
                {[1, 2].map((i) => (
                  <Skeleton
                    p={12}
                    key={i}
                    height={124}
                    mb={0}
                  />
                ))}
              </Stack>
            }
          </ScrollArea>
          <Box fz="14px" p={"sm"} className="sidebar-search-popover-footer">
            {" "}
            {searchResults.length} results found{" "}
          </Box>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

function IdeaSearchResult({
  idea,
  onClick,
  variant = "header",
}: {
  idea: any;
  onClick: () => void;
  variant?: "header" | "sidebar";
}) {
  const isSidebar = variant === "sidebar";
  const titleW = isSidebar ? "100%" : "470px";
  const bodyW = isSidebar ? "100%" : "530px";

  return (
    <Card
      withBorder
      p="xs"
      my="xs"
      className="sidebar-search-result-card"
      onClick={onClick}
    >
      <Flex justify="space-between" align="flex-start" gap="sm" wrap="nowrap" mb={4}>
        <Flex align="center" miw={0} className="sidebar-search-result-header-left">
          <Box className="sidebar-search-result-chat-icon">
            <IconMessage color="#5B60E3" stroke={1.5} />
          </Box>
          <Text
            w={titleW}
            lineClamp={1}
            fz="sm"
            className="sidebar-search-result-title"
          >
            {idea?.title || "Unassigned"}
          </Text>
        </Flex>
        <Box className="sidebar-search-result-open-icon" aria-hidden>
          <IconArrowUpRight size={20} stroke={1.5} color='#161832'/>
        </Box>
      </Flex>

      <Box w={bodyW} maw="100%">
        <Spoiler
          maxHeight={20}
          showLabel="see more"
          hideLabel="Hide"
          className="sidebar-search-spoiler"
          my="xs"
        >
          {stripHtmlFromIdeaDescription(idea?.description)}
        </Spoiler>
      </Box>
      <Flex align="center" justify="space-between">
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Owner
          </Text>
          <Text  fz="xs" fw={500} tt="capitalize" >
            {idea?.owner?.name || '-'}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Status
          </Text>
          <Text fz="xs" fw={500} tt="capitalize">
            {idea?.status}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Priority
          </Text>
          <Text fz="xs" fw={500} tt="capitalize">
            {idea?.priority || "-"}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Product area
          </Text>
          <Text fz="xs" fw={500} tt="capitalize">
            {idea?.componentObj?.name|| "-"}
          </Text>
        </Box>
        <Box mr="sm">
          <Text fz="xs" c="dimmed">
              Accounts
          </Text>
          <Text fz="xs" fw={500}>
            {idea?.aggregations?.accountsCount?.total || "-"}
          </Text>
        </Box>
      </Flex>
    </Card>
  );
}


export default Search;
